import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { useSnackbar } from "notistack";
import { api } from "../../services/Api";
import ConfirmModal from "../../components/ConfirmModal";

const COLUNAS = [
  { id: "mnemonico", label: "Mnemonico" },
  {
    id: "minutosIncubacao",
    label: "Minutos de incubação",
    align: "right",
    width: 196,
  },
];

export default function ConfiguracaoIncubacaoMnemonicos() {
  const { enqueueSnackbar } = useSnackbar();
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [mnemonico, setMnemonico] = useState("");
  const [minutosIncubacao, setMinutosIncubacao] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [linhaParaExcluir, setLinhaParaExcluir] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await api.get("/v2/mapaTrace/mnemonicos");
      const dados = res.data?.data;
      setLista(Array.isArray(dados) ? dados : []);
    } catch (e) {
      setLista([]);
      const msg = (e.response?.data?.messages ?? "").trim();
      if (msg) {
        enqueueSnackbar(msg, { variant: "error", autoHideDuration: 4000 });
      }
    } finally {
      setCarregando(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  function abrirNovo() {
    setEditandoId(null);
    setMnemonico("");
    setMinutosIncubacao("");
    setDialogAberto(true);
  }

  function abrirEditar(row) {
    setEditandoId(row.id);
    setMnemonico(row.mnemonico ?? "");
    setMinutosIncubacao(String(row.minutosIncubacao ?? ""));
    setDialogAberto(true);
  }

  function fecharDialog() {
    if (salvando) return;
    setDialogAberto(false);
  }

  async function handleSalvar() {
    const codigo = mnemonico.trim().toUpperCase();
    const minutosNum = Number(minutosIncubacao);

    if (!codigo) {
      enqueueSnackbar("Informe o mnemonico.", { 
        variant: "warning", 
      });
      return;
    }

    if (!Number.isInteger(minutosNum)) {
      enqueueSnackbar("Minutos devem ser um número inteiro válido.", {
        variant: "warning",
      });
      return;
    }

    setSalvando(true);
    try {
      if (editandoId) {
        await api.patch(`/v2/mapaTrace/mnemonicos/${editandoId}`, {
          mnemonico: codigo,
          minutos_incubacao: minutosNum,
        });
        enqueueSnackbar("Registro atualizado.", { variant: "success" });
      } else {
        await api.post("/v2/mapaTrace/mnemonicos", {
          mnemonico: codigo,
          minutos_incubacao: minutosNum,
        });
        enqueueSnackbar("Registro criado.", { variant: "success" });
      }
      setDialogAberto(false);
      await carregar();
    } catch (e) {
      const msg = (e.response?.data?.messages ?? "").trim();
      enqueueSnackbar(msg || "Não foi possível salvar.", {
        variant: "error",
        autoHideDuration: 4000,
      });
    } finally {
      setSalvando(false);
    }
  }

  function fecharConfirmarExclusao() {
    setLinhaParaExcluir(null);
  }

  async function confirmarExclusao() {
    const row = linhaParaExcluir;
    if (!row) return;
    setLinhaParaExcluir(null);
    try {
      await api.delete(`/v2/mapaTrace/mnemonicos/${row.id}`);
      enqueueSnackbar("Registro removido.", { variant: "success" });
      await carregar();
    } catch (e) {
      const msg = (e.response?.data?.messages ?? "").trim();
      enqueueSnackbar(msg || "Não foi possível remover.", {
        variant: "error",
        autoHideDuration: 4000,
      });
    }
  }

  return (
    <Box className="mapa-trace-config-incubacao" sx={{ mt: 2, mb: 4 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6" component="h2">
          Tempos de incubação por mnemonico
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={abrirNovo}
        >
          Novo cadastro
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Cadastro global: cada mnemonico pode ter um tempo padrão de incubação
        (minutos), usado na consulta de tubo e no fluxo de amostras.
      </Typography>

      {carregando ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={36} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                {COLUNAS.map((col) => (
                  <TableCell
                    key={col.id}
                    align={col.align || "left"}
                    sx={{
                      ...(col.width != null
                        ? { width: col.width, maxWidth: col.width }
                        : {}),
                      ...(col.id === "minutosIncubacao"
                        ? {
                            whiteSpace: "nowrap",
                            pr: 0.75,
                            verticalAlign: "middle",
                          }
                        : {}),
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
                <TableCell
                  align="right"
                  sx={{ width: 108, pl: 0.75, verticalAlign: "middle" }}
                >
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhum mnemonico cadastrado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                lista.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell align="left" sx={{ verticalAlign: "middle" }}>
                      {row.mnemonico}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        verticalAlign: "middle",
                        width: 196,
                        maxWidth: 196,
                        pr: 0.75,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row.minutosIncubacao}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        verticalAlign: "middle",
                        width: 108,
                        pl: 0.75,
                      }}
                    >
                      <IconButton
                        size="small"
                        aria-label={`Editar ${row.mnemonico}`}
                        onClick={() => abrirEditar(row)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label={`Excluir ${row.mnemonico}`}
                        onClick={() => setLinhaParaExcluir(row)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmModal
        open={Boolean(linhaParaExcluir)}
        onClose={fecharConfirmarExclusao}
        title="Excluir cadastro"
        text={
          linhaParaExcluir
            ? `Remover o mnemonico "${linhaParaExcluir.mnemonico}" e seu tempo cadastrado?`
            : ""
        }
        cancelButtonLabel="Não, quero voltar"
        confirmButtonLabel="Sim, desejo excluir"
        onCancel={fecharConfirmarExclusao}
        onConfirm={confirmarExclusao}
      />

      <Dialog
        open={dialogAberto}
        onClose={fecharDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {editandoId ? "Editar cadastro" : "Novo cadastro"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Mnemonico"
            fullWidth
            value={mnemonico}
            onChange={(e) => setMnemonico(e.target.value)}
            disabled={salvando}
          />
          <TextField
            margin="dense"
            label="Minutos de incubação"
            type="number"
            fullWidth
            value={minutosIncubacao}
            onChange={(e) => setMinutosIncubacao(e.target.value)}
            disabled={salvando}
            inputProps={{
              step: 1,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDialog} disabled={salvando}>
            Cancelar
          </Button>
          <Button
            onClick={handleSalvar}
            variant="contained"
            disabled={salvando}
          >
            {salvando ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
